/**
 * The signed-in user's view of the app — GET /api/v1/me — and nothing else.
 *
 * HARD RULE (ADR §1.4): screens decide what to show from `capabilities` and `menu`, never from a role or a
 * person. `can()` is the only permission question a component may ask.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Capability, MeResponse } from '@amplispot/contracts';
import { call } from '../api';

export const ME_KEY = ['me'] as const;

export function useMeQuery(): UseQueryResult<MeResponse> {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: () => call('me.get'),
    retry: false,
    staleTime: 60_000,
  });
}

interface Session {
  me: MeResponse;
  can: (c: Capability) => boolean;
  /** True while viewing as someone (ADR-006 `view` mode): every write is refused, so no write control shows. */
  readOnly: boolean;
}

const Ctx = createContext<Session | null>(null);

/**
 * Capabilities that only read. In view mode the server refuses every non-GET with 403 "…read-only…" but still
 * returns the target's full capability list (so the menu matches theirs); the SPA keeps only these, which
 * hides every button, inline editor and form that would write.
 */
export const isReadCapability = (c: Capability): boolean =>
  /\.read(\.|$)/.test(c) || c.startsWith('today.') || c === 'changeRequest.view';

export function SessionProvider({ me, children }: { me: MeResponse; children: ReactNode }) {
  const readOnly = me.acting_as?.mode === 'view';
  const caps = new Set(readOnly ? me.capabilities.filter(isReadCapability) : me.capabilities);
  return <Ctx.Provider value={{ me, can: (c) => caps.has(c), readOnly }}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession must be used inside the signed-in app');
  return s;
}
